import { getAllLevels, getLevelDomains } from '../api/levels';
import { getUserSpecializations } from '../api/users_institutions';
import { isStaff, isAdmin } from '../api/users_institutions';
import { getCachedDashboardGridContent, prefetchDashboardGridContent } from '../api/dashboardGridCache';

/**
 * Whether a level is locked for a user based on their specializations vs the level's allowed
 * domains -- the same formula used for the lock icon on the level-card grid
 * (DashboardGrid.jsx's LevelCard). A level with no matching domain, or a user with no
 * specializations, is locked by default (opt-in access only) -- unless the user holds an
 * explicit LevelAdminPermission for this level, which always unlocks it regardless of
 * specialization/domain matching.
 */
export function isLevelLockedForUser(levelId, specializations, levelDomains) {
    const domains = levelDomains.filter((domain) => Number(domain.level?.id ?? domain.level) === Number(levelId));
    return specializations.length === 0 || !specializations.some((spec) =>
        domains.some((domain) =>
            String(spec.target_domain || '').trim().toLowerCase() === String(domain.domain || '').trim().toLowerCase()
        )
    );
}

/**
 * Resolves a level by its URL slug (name) and checks whether it's locked for the given user.
 * Used to guard direct/typed/bookmarked URL visits to a level -- or to a chapter/exam/test nested
 * under one -- that the normal dashboard click flow would have hidden behind a lock icon.
 *
 * This is a client-side UX guard only, matching the existing lock icon (also purely
 * client-side/cosmetic -- none of the backend endpoints enforce specialization/domain access).
 * It doesn't prevent access to the underlying data, only redirects the page away.
 *
 * @returns {Promise<boolean>} true if locked, false if unlocked OR the level name doesn't
 *   resolve (in which case the page's own "not found" handling should take over instead).
 */
export async function isLevelNameLockedForUser(levelName, userId) {
    const [levels, specializations, levelDomains] = await Promise.all([
        getAllLevels(),
        getUserSpecializations(userId),
        getLevelDomains(),
    ]);
    const level = levels.find(l => l.name === levelName);
    if (!level) return false;
    if (isStaff()) return false;
    return isLevelLockedForUser(level.id, specializations, levelDomains);
}

/**
 * Whether the given user may turn editor mode ON *inside* a level (Level.jsx and everything
 * nested under it), as opposed to merely opening/reading it.
 *
 * Admins and superadmins can open any level, including ones in a category aimed at "teachers" --
 * but a level outside an "admins" category is read-only for them: it's teacher material, so
 * editing it isn't on the table regardless of who holds a LevelAdminPermission for it. Inside an
 * "admins" category a superadmin always edits, while a plain staff admin needs an explicit
 * LevelAdminPermission row for that level (the same row DashboardGrid's LevelCard requires before
 * it even shows them the card).
 *
 * Client-side gating only -- it hides/disables the editor UI, it doesn't stop the write
 * endpoints, same as every other permission check around this page.
 *
 * Sources levels/categories/levelAdminPermissions from dashboardGridCache rather than fetching
 * them fresh -- a Level page is almost always reached by clicking a card on the Dashboard, so
 * that cache is normally already warm, which is what lets the editor HUD (Level.jsx's
 * adminLevelCanEdit) appear instantly instead of after its own network round trip. Falls back to
 * prefetchDashboardGridContent() (shared with any other in-flight caller, fetching only if truly
 * cold) on a direct/bookmarked link that skipped the Dashboard.
 */
export async function checkAdminLevelEditorPermission(levelName, userId) {
    if (!isStaff() && !isAdmin()) return false;
    const { levels, categories, levelAdminPermissions: permissions } =
        getCachedDashboardGridContent() || await prefetchDashboardGridContent();
    const level = levels.find(l => l.name === levelName);
    if (!level) return false;
    const category = categories.find(cat => Number(cat.id) === Number(level.category?.id ?? level.category));
    if (!category || category.audience !== 'admins') return false;
    if (isAdmin()) return true;
    return permissions.some((perm) =>
        Number(perm.level?.id ?? perm.level) === Number(level.id) &&
        Number(perm.user?.id ?? perm.user) === Number(userId)
    );
}